import React, { useState, useEffect, useCallback } from "react";
import { Box, Skeleton, Typography, Stack } from "@mui/material";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";

const PIE_COLORS = [
  "#3B82F6",
  "#06B6D4",
  "#8B5CF6",
  "#F59E0B",
  "#10B981",
  "#EF4444",
  "#EC4899",
  "#6366F1",
];

const formatValue = (value) => {
  if (value == null) return "";
  const num = Number(String(value).replace(/,/g, ""));
  if (isNaN(num)) return value;
  return Math.round(num);
};

const truncateName = (name, max = 14) => {
  const str = String(name ?? "");
  return str.length > max ? str.slice(0, max) + "…" : str;
};

const RADIAN = Math.PI / 180;

const renderCustomLabel = ({
  cx,
  cy,
  midAngle,
  outerRadius,
  percent,
  name,
}) => {
  const radius = outerRadius + 24;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text
      x={x}
      y={y}
      fill="#4D5F6E"
      textAnchor={x > cx ? "start" : "end"}
      dominantBaseline="central"
      fontSize={12}
      fontWeight={500}
    >
      {truncateName(name)} ({(percent * 100).toFixed(0)}%)
    </text>
  );
};

const formatKey = (key) =>
  String(key || "")
    .replace(/,/g, "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

const SKIP_KEYS = new Set([
  "name",
  "value",
  "valueKey",
  "fill",
  "stroke",
  "cx",
  "cy",
  "innerRadius",
  "outerRadius",
  "startAngle",
  "endAngle",
  "paddingAngle",
  "midAngle",
  "percent",
  "tooltipPayload",
  "tooltipPosition",
  "payload",
]);

const CustomTooltipContent = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const entry = payload[0].payload;
  const { name, value } = entry;
  const color = entry.fill || payload[0].color;
  const percent = payload[0].percent;

  // Collect all extra fields from the data point
  const extraFields = Object.entries(entry).filter(
    ([key]) => !SKIP_KEYS.has(key),
  );

  return (
    <Box
      sx={{
        bgcolor: "#fff",
        border: "1px solid #E8ECF0",
        borderRadius: "8px",
        p: "10px 14px",
        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
        maxWidth: 280,
      }}
    >
      {/* Title with color dot */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            bgcolor: color,
            flexShrink: 0,
          }}
        />
        <Typography sx={{ fontSize: 13, color: "#1A2025", fontWeight: 600 }}>
          {name}
        </Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          gap: 2,
          mb: 0.25,
        }}
      >
        <Typography sx={{ fontSize: 12, color: "#4D5F6E" }}>
          {formatKey(entry.valueKey) || "Value"}
        </Typography>
        <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#1A2025" }}>
          {formatValue(value)}
        </Typography>
      </Box>

      {/* Percentage */}
      {percent != null && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            gap: 2,
            mb: 0.25,
          }}
        >
          <Typography sx={{ fontSize: 12, color: "#4D5F6E" }}>Share</Typography>
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#354ACD" }}>
            {(percent * 100).toFixed(1)}%
          </Typography>
        </Box>
      )}

      {/* All additional data fields */}
      {extraFields.map(([key, val]) => {
        const isItemList =
          Array.isArray(val) &&
          val.length > 0 &&
          val.every(
            (x) => x && typeof x === "object" && "name" in x && "value" in x,
          );

        if (isItemList) {
          return (
            <Box key={key} sx={{ mt: 0.5 }}>
              <Typography sx={{ fontSize: 12, color: "#667F93", mb: 0.5 }}>
                {formatKey(key)}
              </Typography>
              {val.map((item, i) => (
                <Box
                  key={`${item?.name ?? "item"}-${i}`}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 2,
                    pl: 1,
                    mb: 0.25,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 11,
                      color: "#4D5F6E",
                      flex: 1,
                      minWidth: 0,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {String(item?.name ?? "-")}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 11,
                      fontWeight: 500,
                      color: "#1A2025",
                      flexShrink: 0,
                    }}
                  >
                    {typeof item?.value === "number"
                      ? String(Math.round(item.value))
                      : String(item?.value ?? "-")}
                  </Typography>
                </Box>
              ))}
            </Box>
          );
        }

        // Any other array → short summary instead of stringifying
        const display = Array.isArray(val)
          ? `${val.length} item${val.length === 1 ? "" : "s"}`
          : typeof val === "number"
            ? String(Math.round(val))
            : typeof val === "object" && val !== null
              ? "-"
              : String(val ?? "-");

        return (
          <Box
            key={key}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              gap: 2,
              mb: 0.25,
            }}
          >
            <Typography sx={{ fontSize: 12, color: "#667F93", flexShrink: 0 }}>
              {formatKey(key)}
            </Typography>
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 500,
                color: "#1A2025",
                textAlign: "right",
                wordBreak: "break-word",
              }}
            >
              {display}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};

const CustomLegend = ({ data, getSliceColor }) => (
  <Box
    sx={{
      display: "flex",
      flexWrap: "wrap",
      justifyContent: "center",
      gap: "8px 20px",
      pt: 1,
      px: 1,
    }}
  >
    {data.map((entry, i) => (
      <Stack key={i} direction="row" spacing={0.75} alignItems="center">
        <Box
          sx={{
            width: 10,
            height: 10,
            borderRadius: "2px",
            bgcolor: getSliceColor(entry, i),
            flexShrink: 0,
          }}
        />
        <Typography sx={{ fontSize: 12, color: "#4D5F6E", fontWeight: 500 }}>
          {truncateName(entry.name, 18)}
        </Typography>
      </Stack>
    ))}
  </Box>
);

const CustomPieSkeleton = ({ outerRadius, innerRadius, itemCount }) => (
  <Box
    sx={{
      width: "100%",
      height: 340,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    <Box
      sx={{
        position: "relative",
        width: outerRadius * 2,
        height: outerRadius * 2,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Skeleton
        variant="circular"
        width={outerRadius * 2}
        height={outerRadius * 2}
      />
      <Box
        sx={{
          position: "absolute",
          width: innerRadius * 2,
          height: innerRadius * 2,
          bgcolor: "background.default",
          borderRadius: "50%",
        }}
      />
    </Box>
    <Box
      sx={{
        display: "flex",
        gap: 3,
        mt: 3,
        flexWrap: "wrap",
        justifyContent: "center",
      }}
    >
      {Array.from({ length: itemCount }).map((_, i) => (
        <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Skeleton variant="circular" width={10} height={10} />
          <Skeleton variant="text" width={70} height={18} />
        </Box>
      ))}
    </Box>
  </Box>
);

const CustomPieGraph = ({
  chartData,
  colors,
  innerRadius,
  outerRadius,
  threshold,
  thresholdColor = "#FF4D4F",
  belowThresholdColor,
}) => {
  const hasThreshold = threshold != null && !isNaN(Number(threshold));

  const getSliceColor = (entry, index) => {
    if (!hasThreshold) return colors[index % colors.length];
    return Number(entry.value) >= Number(threshold)
      ? thresholdColor
      : belowThresholdColor || colors[index % colors.length];
  };

  return (
    <Box
      sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}
    >
      <ResponsiveContainer width="100%" height={280}>
        <PieChart>
          <Pie
            data={chartData}
            dataKey="value"
            nameKey="name"
            cx="50%"
            cy="50%"
            innerRadius={innerRadius}
            outerRadius={outerRadius}
            paddingAngle={2}
            label={renderCustomLabel}
            labelLine={{
              stroke: "#D0D5DD",
              strokeWidth: 1,
            }}
          >
            {chartData.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={getSliceColor(entry, index)}
                stroke="#fff"
                // strokeWidth={2}
              />
            ))}
          </Pie>
          <Tooltip content={<CustomTooltipContent />} />
        </PieChart>
      </ResponsiveContainer>

      <CustomLegend data={chartData} getSliceColor={getSliceColor} />
    </Box>
  );
};

const CustomPieChart = ({
  data = [],
  colors = PIE_COLORS,
  innerRadius = 60,
  outerRadius = 95,
  refreshTrigger,
  threshold,
  thresholdColor,
  belowThresholdColor,
}) => {
  const [loading, setLoading] = useState(false);
  const [chartData, setChartData] = useState(data);

  const processChartData = (rawData, limit = 6) => {
    if (rawData.length <= limit) return rawData;

    const sorted = [...rawData].sort((a, b) => b.value - a.value);
    const top = sorted.slice(0, limit);
    return [...top];
  };

  useEffect(() => {
    setChartData(processChartData(data, 6));
  }, [data]);

  const handleRefresh = useCallback(() => {
    setLoading(true);
    setTimeout(() => {
      setChartData([...data]);
      setLoading(false);
    }, 1500);
  }, [data]);

  useEffect(() => {
    if (refreshTrigger) {
      handleRefresh();
    }
  }, [refreshTrigger, handleRefresh]);

  return (
    <Box
      sx={{
        background: "#fff",
        borderRadius: 4,
        height: "100%",
        p: "16px 8px",
      }}
    >
      {loading ? (
        <CustomPieSkeleton
          outerRadius={outerRadius}
          innerRadius={innerRadius}
          itemCount={chartData.length}
        />
      ) : (
        <CustomPieGraph
          chartData={chartData}
          colors={colors}
          innerRadius={innerRadius}
          outerRadius={outerRadius}
          threshold={threshold}
          thresholdColor={thresholdColor}
          belowThresholdColor={belowThresholdColor}
        />
      )}
    </Box>
  );
};

export default CustomPieChart;
